import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { SnackbarService } from '../../shared/ui/snackbar/snackbar.service';
import { httpErrorMessage } from './http-error';

/**
 * Single source of truth for user-facing transient notifications.
 *
 * Wraps SnackbarService (the house custom toast primitive) with a thin
 * contract that consolidates :
 *  - error-message extraction from HttpErrorResponse, Error, or raw string
 *    inputs with i18n-aware status-code messages ;
 *  - a stable public surface (`showError`, `showSuccess`, `showInfo`) that
 *    the `errorInterceptor` and any feature component can consume.
 *
 * Replaces the previous MatSnackBar adapter; the public method shape is
 * unchanged so consumers do not move.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly snackbar = inject(SnackbarService);

  showError(error: HttpErrorResponse | Error | string): void {
    // Delegates to the shared layered mapper (business code -> HTTP status ->
    // safe server message) so the toast and the page banners read identically.
    this.snackbar.show(httpErrorMessage(error), { variant: 'error', duration: 5000 });
  }

  showSuccess(message: string): void {
    this.snackbar.show(message, { variant: 'success' });
  }

  showInfo(message: string): void {
    this.snackbar.show(message, { variant: 'info' });
  }

  /**
   * Le geste a abouti, mais quelque chose mérite d'être lu avant de passer à la
   * suite — typiquement une saisie acceptée qui n'aura pas l'effet attendu.
   *
   * Tenu plus longtemps qu'une confirmation : ces messages expliquent une
   * conséquence et se lisent en une phrase ou deux, pas d'un coup d'œil.
   */
  showWarning(message: string): void {
    this.snackbar.show(message, { variant: 'error', duration: 12000 });
  }
}
